import { useEffect, useState } from 'react';
import { db } from '../services/db';
import { captureSilentError } from '../services/posthog';
import { useUIStore } from '../stores/useUIStore';
import { User } from '../types';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || 
    'BPpkfdxKYtXJmw9jbGBeRIJxZedI3hgvrngsoSZYJdXd19dISUId3Mh75jPyfdvm_I8rvgSvYnv3P4cKk_TwZPo';

export const useNotifications = (currentUser: User | null) => {
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission>('default');

  // Check initial permission status safely
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const current = Notification.permission;
      setPermissionStatus(current);
    } else {
      setPermissionStatus('denied'); // Or a special 'unsupported' state if preferred
    }
  }, []);

  const requestPermission = async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('Notifications not supported in this environment');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);

      // subscribe to push if granted
      if (permission === 'granted') {
          await subscribeToPush();
      }
    } catch (e) {
      captureSilentError('useNotifications.requestPermission', e);
    }
  };

  const subscribeToPush = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window && currentUser) {
          try {
              const registration = await navigator.serviceWorker.ready;
              const vapidPublicKey = VAPID_PUBLIC_KEY;
              
              const subscription = await registration.pushManager.subscribe({
                  userVisibleOnly: true,
                  applicationServerKey: vapidPublicKey
              });
              
              await db.savePushSubscription(currentUser.uid, subscription);
          } catch (err) {
              captureSilentError('useNotifications.subscribeToPush', err);
          }
      }
  };


  useEffect(() => {
    if (!currentUser) return;

    // 1. Fetch unread notification count for badge
    db.getUnreadNotificationCount(currentUser.uid)
      .then(count => useUIStore.getState().setUnreadNotificationCount(count))
      .catch((e) => captureSilentError('useNotifications.getUnreadCount', e));

    // 2. Auto-subscribe to Push if already granted
    // This ensures that if the service worker unregisters or we need a new token, 
    // we get it silently without user interaction if they already said yes.
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        subscribeToPush();
    }

    // 3. Subscribe to Realtime (In-App / Foreground)
    // We safeguard this so we only create Notification objects if supported and granted
    const unsubscribe = db.subscribeToNotifications(currentUser.uid, (type, payload) => {
      // Refresh unread count when a realtime notification comes in
      db.getUnreadNotificationCount(currentUser.uid)
        .then(count => useUIStore.getState().setUnreadNotificationCount(count))
        .catch((e) => captureSilentError('useNotifications.getUnreadCount', e));
      
      // Foreground browser notifications are only needed when the app is not visible.
      // If the user is actively viewing the app, the in-app badge is sufficient.
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (document.visibilityState === 'visible') return; // App is in foreground, skip system notification.

      if (type === 'REQUEST') {
        new Notification('New Friend Request', {
          body: `${payload.sender.displayName} wants to add you!`,
          icon: '/icon.png', // Ensure this path is correct
          tag: 'friend-request'
        });
      } else if (type === 'SHOUT') {
        new Notification('New Shout Out!', {
          body: `${payload.sender.displayName} gave you a Good Shout! 🙌`,
          icon: '/icon.png',
          tag: 'shout-out'
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [currentUser]);

  return {
      permissionStatus,
      requestPermission
  };
};
